// src/hooks/useFieldImagery.js
//
// Imagery for the FieldAssist views. One hook, mounted by /field/route and
// /field/site, owning three things: what imagery this org may see HERE, the
// raster layer on the caller's map, and the three-state answer the toggle
// renders.
//
// ── WHY THIS IS NOT ImageryTab ─────────────────────────────────────────────
//
// ImageryTab assumes the app shell — the layer panel, the layer registry, the
// stack manager, useSyncRegistryLayers. /field/route and /field/site are
// shell-less full-viewport maps off fieldRoute, so none of that exists. What is
// reusable is the STORE, and that is what this hook reuses.
//
// ── THE STORE COLLAPSES PER-DATE ENTITLEMENT AND THIS VIEW CANNOT ─────────
//
// This is the whole reason the hook holds its own copy of the footprints FC.
//
// authz-api ships `entitlement` PER FEATURE — one feature per dataset, each
// carrying its own scope, coverage_aoi_id and valid_to (index.js:888).
// groupFootprintsIntoSeries then hoists it to the SERIES:
//
//     entitlement: normalizeEntitlement(p.entitlement)   // from the FIRST
//                                                        // feature of the series
//
// For an org whose entitlement is uniform across a slug that is lossless. For
// Earl O&G it is not: one slug, `imagery.permian.midland`, and
//
//     2025_12_13   scope=full     active
//     2026_03_01   scope=aoi      active   (clipped to a coverage AOI)
//     2026_03_01   scope=full     REVOKED  (filtered out by the API)
//     2025_09_18   no row at all            (absent from their catalog)
//
// so the series-level value is whichever date happened to arrive first and is
// wrong for at least one of the others. A single boolean, or a single series
// entitlement, cannot express that. Hence `entitlementByDatasetId` below.
//
// This is NOT a defect in the store — a series-level entitlement is the right
// shape for the catalog strip it feeds. It is the wrong shape for this toggle,
// and rather than change a store four other views depend on, the hook keeps
// the per-feature detail the API already sent and the store discarded.
//
// ── ONE FETCH, NOT TWO ────────────────────────────────────────────────────
//
// loadCatalog() takes an injectable fetcher (its signature is `options` OR a
// function). Passing one that tees the raw FC on its way through means the
// store gets its catalog and this hook gets the per-feature entitlement from
// the SAME request. The alternative — letting the store fetch, then fetching
// again for entitlement — would double a ~108 kB payload on a phone on
// Starlink, to re-read data already in the browser.
//
// The fetch is re-implemented here because defaultFootprintsFetcher is module-
// private to the store. `lod=fine` is copied deliberately and is load-bearing:
// the store's own note records that the default `coarse` is the dataset
// ENVELOPE, up to 11.9x the real coverage on this corpus, and that at one
// measured point coarse said both current datasets covered and fine said
// neither did. A field view answering "is there imagery here" off the envelope
// would be confidently wrong over ground that renders blank.

import { useCallback, useEffect, useRef, useState } from "react";
import { useImageryStackStore } from "../stores/useImageryStackStore";
import { useAuthStore } from "../stores/useAuthStore";
import { getOrFetchTileToken } from "../api/tileTokens";
import { tileUrl } from "../api/tileUrl";

const SRC_IMAGERY = "field-imagery";
const LAYER_IMAGERY = "field-imagery-raster";

/**
 * @typedef {"available"|"clipped"|"unavailable"|"loading"} ImageryState
 */

// Raw footprints fetch. See the header for why this is not imported.
async function fetchFootprints() {
  const token = await useAuthStore.getState().ensureFreshToken();
  if (!token) throw new Error("no_auth_token");
  const res = await fetch("/imagery/footprints?lod=fine", {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`footprints_${res.status}`);
  return res.json();
}

/**
 * @param {{ current: import("maplibre-gl").Map | null }} mapRef
 * @param {boolean} mapReady
 */
export function useFieldImagery(mapRef, mapReady) {
  const catalog = useImageryStackStore((s) => s.catalog);
  const loading = useImageryStackStore((s) => s.loading);

  // dataset_id -> { scope, coverage_aoi_id, valid_to, status }
  const [entitlementByDatasetId, setEntitlementByDatasetId] = useState(null);
  const [on, setOn] = useState(false);
  const [activeKey, setActiveKey] = useState(null);   // dataset_key
  const [error, setError] = useState(null);
  const loadedRef = useRef(false);

  // ── catalog + entitlement, one request ───────────────────────────────────
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    let cancelled = false;

    useImageryStackStore.getState().loadCatalog(async () => {
      const fc = await fetchFootprints();
      if (!cancelled) {
        const m = new Map();
        for (const f of fc?.features ?? []) {
          const p = f?.properties;
          if (p?.dataset_id && p?.entitlement) m.set(p.dataset_id, p.entitlement);
        }
        setEntitlementByDatasetId(m);
      }
      return fc;
    }).catch((e) => {
      if (!cancelled) setError(String(e?.message || e));
    });

    return () => { cancelled = true; };
  }, []);

  // ── the acquisitions this org may see, newest first ──────────────────────
  //
  // Flattened across series. FieldAssist targets one basin and the corpus is a
  // single slug; a series picker on a phone would be chrome for a distinction
  // this product does not yet make.
  const acquisitions = [];
  for (const s of catalog?.series ?? []) {
    for (const a of s.acquisitions ?? []) {
      const ent = entitlementByDatasetId?.get(a.dataset_id) ?? null;
      acquisitions.push({
        dataset_key: a.dataset_key,
        dataset_id: a.dataset_id,
        acquisition_date: a.acquisition_date,
        maxzoom: a.maxzoom ?? 20,
        // scope is the API's, not a guess. Absent entitlement means the FC
        // carried a feature with no entitlement object, which should not
        // happen — the query INNER JOINs entitlements — so it is treated as
        // unknown-and-therefore-unavailable rather than assumed full.
        scope: ent?.scope === "aoi" ? "aoi" : ent?.scope === "full" ? "full" : null,
        coverage_aoi_id: ent?.coverage_aoi_id ?? null,
        valid_to: ent?.valid_to ?? null,
      });
    }
  }
  acquisitions.sort((a, b) => b.acquisition_date.localeCompare(a.acquisition_date));

  const active = acquisitions.find((a) => a.dataset_key === activeKey)
    ?? acquisitions[0]
    ?? null;

  // ── the three states ─────────────────────────────────────────────────────
  //
  // A DATE THIS ORG CANNOT SEE IS NOT IN `acquisitions` AT ALL. /imagery/
  // footprints INNER JOINs entitlements, so Earl O&G's 2025_09_18 — which
  // DreamHammer has and they do not — never arrives. "unavailable" is therefore
  // reached two ways: an empty list, or a mint that came back 403. Both are the
  // same thing to the person holding the phone, and both say so rather than
  // rendering a toggle that does nothing.
  /** @type {ImageryState} */
  let state;
  if (loading || entitlementByDatasetId === null) state = "loading";
  else if (error) state = "unavailable";
  else if (!active || !active.scope) state = "unavailable";
  else if (active.scope === "aoi") state = "clipped";
  else state = "available";

  // ── the raster layer ─────────────────────────────────────────────────────
  //
  // Same path as ReviewMap and CellMap, the two other standalone maps in this
  // app: /tile/session for a cache key, then /tiles/WebMercatorQuad with the
  // key in the URL. THE TILE TOKEN NEVER ENTERS THE URL — it rides as
  // X-Tile-Token through the map's transformRequest, which is what keeps the
  // URL stable so a token refresh does not re-tile the whole viewport.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    let cancelled = false;

    const detach = () => {
      if (!mapRef.current) return;
      if (map.getLayer(LAYER_IMAGERY)) map.removeLayer(LAYER_IMAGERY);
      if (map.getSource(SRC_IMAGERY)) map.removeSource(SRC_IMAGERY);
    };

    if (!on || state === "unavailable" || state === "loading" || !active) {
      detach();
      return;
    }

    (async () => {
      try {
        const { ck } = await getOrFetchTileToken(active.dataset_key);
        if (cancelled || !mapRef.current) return;
        const url = tileUrl(active.dataset_key, ck);

        const existing = map.getSource(SRC_IMAGERY);
        if (!existing) {
          map.addSource(SRC_IMAGERY, {
            type: "raster", tiles: [url], tileSize: 256, maxzoom: active.maxzoom,
          });
          // Directly above the basemap and below everything this view draws,
          // so pads, routes and asset markers stay on top of the photograph.
          const firstOwn = map.getStyle().layers.find((l) => l.id !== "base");
          map.addLayer({ id: LAYER_IMAGERY, type: "raster", source: SRC_IMAGERY },
            firstOwn ? firstOwn.id : undefined);
        } else if (existing.tiles?.[0] !== url) {
          existing.setTiles([url]);
        }
        if (!cancelled) setError(null);
      } catch (e) {
        // SURFACE THE 403, DO NOT ASSUME SUCCESS.
        //
        // /imagery/footprints and /tile/session do not run identical
        // predicates, and the four gaps are real even though every one is
        // zero-risk on this database today:
        //
        //   1. orgs.imagery_disabled_at — the org-level imagery kill switch.
        //      assertOrgNotRevoked() enforces it at the mint; the footprints
        //      query does not join orgs at all. (0 orgs disabled today.)
        //   2. entitlement scope='none' — returned by footprints, denied at
        //      the mint as NO_ENTITLEMENT. (0 rows today.)
        //   3. the project gate — ?project_id= runs resolveProjectForUser and
        //      can 403 INVALID_PROJECT. Not knowable from footprints.
        //   4. MULTI-ORG. resolveOrgIdForUser picks the org with `LIMIT 1` and
        //      NO ORDER BY (authz-api/index.js:467). For a user in two orgs
        //      that is nondeterministic, not merely unspecified — the same
        //      request can mint against a different org run to run. 0 multi-org
        //      users today, so it is latent rather than broken; the first
        //      two-org account makes it live.
        //
        // So a mint failure is a real state, not an impossible one, and the
        // toggle reports it instead of leaving a control that silently does
        // nothing.
        if (!cancelled) setError(String(e?.message || e));
      }
    })();

    return () => { cancelled = true; };
  }, [on, state, active?.dataset_key, mapReady, mapRef]);

  // Detach on unmount so a re-entered view does not inherit a stale source.
  useEffect(() => () => {
    const map = mapRef.current;
    if (!map) return;
    try {
      if (map.getLayer(LAYER_IMAGERY)) map.removeLayer(LAYER_IMAGERY);
      if (map.getSource(SRC_IMAGERY)) map.removeSource(SRC_IMAGERY);
    } catch { /* map already torn down */ }
  }, [mapRef]);

  const toggle = useCallback(() => setOn((v) => !v), []);

  return {
    state,
    on: on && (state === "available" || state === "clipped"),
    toggle,
    acquisitions,
    active,
    setActiveKey,
    error,
  };
}
